import { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  CartesianGrid,
  Dot,
} from "recharts";
import { offersClient } from "../lib/offersApi";

/**
 * Line chart of the daily average applied markup. A horizontal reference
 * line shows the target markup (e.g. scenario target of the current offer).
 *
 * Color rules:
 *  - green if avg >= target
 *  - amber if avg within target - 5pp
 *  - red   if avg < target - 5pp
 */
export default function MarginTrendChart({
  days = 30,
  target = null,
  height = 160,
}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    offersClient
      .get(`/reports/margin-trend?days=${days}`)
      .then((r) => setData(r.data.series || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [days]);

  const withBand = data.map((d) => {
    const avg = d.avg_markup;
    let color = "var(--text-muted)";
    if (avg != null && target != null) {
      if (avg >= target) color = "#16A34A";
      else if (avg >= target - 5) color = "#D97706";
      else color = "#DC2626";
    } else if (avg != null) {
      color = "var(--accent-primary)";
    }
    return { ...d, color };
  });

  if (loading) {
    return (
      <div
        className="h-[160px] flex items-center justify-center text-[11px]"
        style={{ color: "var(--text-muted)" }}
      >
        Caricamento…
      </div>
    );
  }
  if (withBand.every((d) => d.avg_markup == null)) {
    return (
      <div
        className="h-[160px] flex items-center justify-center text-[11px]"
        style={{ color: "var(--text-muted)" }}
        data-testid="margin-trend-empty"
      >
        Nessuna offerta con ricarico negli ultimi {days} giorni
      </div>
    );
  }

  return (
    <div style={{ width: "100%", height }} data-testid="margin-trend-chart">
      <ResponsiveContainer>
        <LineChart data={withBand} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#F1F5F9" />
          <XAxis
            dataKey="date"
            tickFormatter={(d) => (d || "").slice(5)}
            tick={{ fontSize: 9, fill: "#94A3B8" }}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontSize: 9, fill: "#94A3B8" }}
            tickFormatter={(v) => `${v}%`}
            width={32}
          />
          <Tooltip
            contentStyle={{
              fontSize: 11,
              padding: "6px 10px",
              background: "#1E293B",
              color: "#fff",
              border: "none",
              borderRadius: 6,
            }}
            labelFormatter={(l) => `📅 ${l}`}
            formatter={(val, _, ctx) => [
              val != null ? `${val}%` : "—",
              `${ctx.payload.count} offerte`,
            ]}
          />
          {target != null && (
            <ReferenceLine
              y={target}
              stroke="#94A3B8"
              strokeDasharray="3 3"
              label={{
                value: `Target ${target}%`,
                fontSize: 9,
                fill: "#94A3B8",
                position: "right",
              }}
            />
          )}
          <Line
            type="monotone"
            dataKey="avg_markup"
            stroke="var(--accent-primary)"
            strokeWidth={2}
            dot={(p) =>
              p?.payload?.avg_markup == null ? null : (
                <Dot
                  {...p}
                  r={3}
                  fill={p.payload.color}
                  stroke={p.payload.color}
                />
              )
            }
            activeDot={{ r: 4 }}
            connectNulls={true}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
